import { useState, useEffect } from "react";
import RegionSelector from "./careers/region-selector";
import CountrySelector from "./careers/country-selector";
import SearchBar from "./careers/search-bar";
import JobList from "./careers/job-list";
import JobDetail from "./careers/job-detail";
import Pagination from "./careers/pagination";
import { isCountryInRegion, getCountriesForRegion } from "./careers/region-selector";
import "./../styles/careers.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_ADMIN_API_URL || "https://ampsweb.saassrs.com";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [region, setRegion] = useState("Europe");
  const [country, setCountry] = useState("all");
  const [countryLabel, setCountryLabel] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasMore: false,
    next: null,
    previous: null,
    isFilteredPagination: false
  });

  const fetchJobs = async (pageNumber = 1) => {
    setLoading(true);
    setError(null);

    try {
      let url;

      if (pageNumber === 1) {
        const ceipalParams = new URLSearchParams();
        ceipalParams.append("page", "1");
        ceipalParams.append("perPage", "20");
        ceipalParams.append("status", "active");
        url = `${API_BASE_URL}/api/jobs?${ceipalParams.toString()}`;
      } else if (pagination.next && pageNumber > pagination.page) {
        const ceipalParams = new URLSearchParams();
        ceipalParams.append("nextUrl", pagination.next);
        url = `${API_BASE_URL}/api/jobs?${ceipalParams.toString()}`;
      } else if (pagination.previous && pageNumber < pagination.page) {
        const ceipalParams = new URLSearchParams();
        ceipalParams.append("nextUrl", pagination.previous);
        url = `${API_BASE_URL}/api/jobs?${ceipalParams.toString()}`;
      } else {
        const firstPageParams = new URLSearchParams();
        firstPageParams.append("page", "1");
        firstPageParams.append("perPage", "20");
        firstPageParams.append("status", "active");
        const firstPageUrl = `${API_BASE_URL}/api/jobs?${firstPageParams.toString()}`;

        try {
          const firstPageResponse = await fetch(firstPageUrl);
          const firstPageData = await firstPageResponse.json();
          if (firstPageData.success && firstPageData.data?.next) {
            const ceipalParams = new URLSearchParams();
            ceipalParams.append("nextUrl", firstPageData.data.next);
            url = `${API_BASE_URL}/api/jobs?${ceipalParams.toString()}`;
          } else {
            throw new Error("Failed to get next URL from page 1");
          }
        } catch (err) {
          setError(`Cannot navigate to page ${pageNumber}. Please refresh the page and try again.`);
          setLoading(false);
          return;
        }
      }

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok || !data.success || data.error) {
        let errorMsg = data.error?.message || data.message || data.error || `Failed to fetch jobs: ${response.statusText}`;
        setError(errorMsg);
        setJobs([]);
        setPagination({
          page: pageNumber,
          limit: 20,
          total: 0,
          totalPages: 0,
          hasMore: false,
          next: null,
          previous: null,
          isFilteredPagination: false
        });
        return;
      }

      const responseData = data.data || {};
      const jobsArray = Array.isArray(responseData.results) ? responseData.results : [];

      const total = responseData.count || 0;
      const currentPage = responseData.page_number || pageNumber;
      const perPage = responseData.limit || 20;
      const totalPages = responseData.num_pages || 1;

      const nextUrlFromApi = responseData.next || data.pagination?.next || null;
      const previousUrlFromApi = responseData.previous || data.pagination?.previous || null;

      let uniqueJobs = removeDuplicateJobs(jobsArray);

      let filteredJobs = uniqueJobs;

      if (country !== "all") {
        const filterCountry = (countryLabel || country).toLowerCase().trim();

        filteredJobs = uniqueJobs.filter(job => {
          const jobCountry = String(job.country || "").toLowerCase().trim();
          return jobCountry === filterCountry || jobCountry.includes(filterCountry);
        });
      }

      if (country === "all" && region && region !== "all" && region !== null) {
        filteredJobs = filterJobsByRegion(filteredJobs, region);
      }

      if (keyword && keyword.trim() !== "") {
        filteredJobs = filterJobsByKeyword(filteredJobs, keyword);
      }

      filteredJobs = removeDuplicateJobs(filteredJobs);

      setJobs(filteredJobs);

      const paginationPage = currentPage || pageNumber;
      setPagination({
        page: paginationPage,
        limit: perPage,
        total: total,
        totalPages: totalPages,
        hasMore: !!nextUrlFromApi,
        next: nextUrlFromApi,
        previous: previousUrlFromApi,
        isFilteredPagination: false
      });
      setError(null);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError(err.message || "Failed to load jobs. Please try again later.");
      setJobs([]);
      setPagination({
        page: pageNumber,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasMore: false,
        next: null,
        previous: null
      });
    } finally {
      setLoading(false);
    }
  };

  const getJobUniqueId = (job) => {
    if (job.job_id) return String(job.job_id).toLowerCase().trim();
    if (job.id) return String(job.id).toLowerCase().trim();
    if (job.external_id) return String(job.external_id).toLowerCase().trim();
    if (job.posting_id) return String(job.posting_id).toLowerCase().trim();
    if (job.job_posting_id) return String(job.job_posting_id).toLowerCase().trim();

    const title = String(job.position_title || job.title || job.job_title || '').toLowerCase().trim();
    const location = String(job.location || '').toLowerCase().trim();
    const city = String(job.city || '').toLowerCase().trim();
    const country = String(job.country || '').toLowerCase().trim();

    return `${title}_${location}_${city}_${country}`;
  };

  const removeDuplicateJobs = (jobs) => {
    if (!Array.isArray(jobs)) {
      return [];
    }

    const seenIds = new Set();
    const uniqueJobs = [];

    for (const job of jobs) {
      const jobId = getJobUniqueId(job);

      if (!seenIds.has(jobId)) {
        seenIds.add(jobId);
        uniqueJobs.push(job);
      }
    }

    return uniqueJobs;
  };

  const filterJobsByRegion = (jobsToFilter, selectedRegion) => {
    if (!selectedRegion || selectedRegion === "all" || selectedRegion === null) {
      return jobsToFilter;
    }

    return jobsToFilter.filter(job => {
      const jobCountry = job.country || job.location_country || job.country_code || "";
      const jobLocation = job.location || "";
      const jobCity = job.city || "";
      const jobState = job.state || "";

      const locationParts = [];
      if (jobCity) locationParts.push(String(jobCity).toLowerCase());
      if (jobState) locationParts.push(String(jobState).toLowerCase());
      if (jobCountry) locationParts.push(String(jobCountry).toLowerCase());
      if (jobLocation) locationParts.push(String(jobLocation).toLowerCase());

      const jobLocationString = locationParts.join(" ");

      if (jobCountry && isCountryInRegion(jobCountry, selectedRegion)) {
        return true;
      }
      if (jobLocation && isCountryInRegion(jobLocation, selectedRegion)) {
        return true;
      }
      const regionCountries = getCountriesForRegion(selectedRegion) || [];
      return regionCountries.some(rc => {
        const rcLower = String(rc).toLowerCase();
        return jobLocationString.includes(rcLower);
      });
    });
  };

  const filterJobsByKeyword = (jobsToFilter, searchKeyword) => {
    if (!searchKeyword || searchKeyword.trim() === "") {
      return jobsToFilter;
    }

    const keywordLower = searchKeyword.toLowerCase().trim();
    return jobsToFilter.filter(job => {
      const jobTitle = (
        job.position_title ||
        job.public_job_title ||
        job.title ||
        job.job_title ||
        ""
      ).toLowerCase();

      const locationParts = [];
      if (job.city) locationParts.push(job.city.toLowerCase());
      if (job.state) locationParts.push(job.state.toLowerCase());
      if (job.country) locationParts.push(String(job.country).toLowerCase());
      if (job.location) locationParts.push(String(job.location).toLowerCase());
      if (job.location_country) locationParts.push(String(job.location_country).toLowerCase());

      const jobLocationString = locationParts.join(" ");

      return (
        jobTitle.includes(keywordLower) ||
        jobLocationString.includes(keywordLower)
      );
    });
  };

  useEffect(() => {
    fetchJobs(page);
  }, [page, region, country, countryLabel, keyword]);

  const handleJobClick = async (job) => {
    if (job && typeof job === 'object') {
      setSelectedJob(job);
      setShowDetail(true);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/jobs/${job}`);
      if (!response.ok) {
        throw new Error("Failed to fetch job details");
      }
      const data = await response.json();
      const jobData = data.results;
      setSelectedJob(jobData);
      setShowDetail(true);
    } catch (err) {
      console.error("Error fetching job details:", err);
      setError(err.message);
    }
  };

  const handleSearch = (searchKeyword) => {
    setKeyword(searchKeyword);
    if (page !== 1) {
      setPage(1);
    }
  };

  const handleRegionChange = (regionData) => {
    if (typeof regionData === 'object' && regionData !== null) {
      setRegion(regionData.value || "all");
    } else {
      setRegion(regionData || "all");
    }
    setCountry("all");
    setCountryLabel(null);
    if (page !== 1) {
      setPage(1);
    }
  };

  const handleCountryChange = (countryData) => {
    if (typeof countryData === 'object' && countryData !== null) {
      setCountry(countryData.value || "all");
      setCountryLabel(countryData.value === "all" ? null : (countryData.label || null));
    } else {
      setCountry(countryData || "all");
      setCountryLabel(null);
    }
    if (page !== 1) {
      setPage(1);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) {
      return;
    }


    if (newPage > page && !pagination.next && newPage > 1) {
      setError(`Cannot navigate to page ${newPage}. Please refresh the page and try again.`);
      return;
    }

    if (newPage < page && !pagination.previous && newPage < pagination.totalPages) {
      setError(`Cannot navigate to page ${newPage}. Please refresh the page and try again.`);
      return;
    }

    if (newPage !== page) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="careers-page">
      <section className="careers-hero">
        <div className="container">
          <div className="careers-hero__content">
            <h1 className="careers-hero__title">Join Our Team</h1>
            <p className="careers-hero__subtitle">
              Discover exciting career opportunities and be part of our growing team
            </p>
          </div>
        </div>
      </section>

      <section className="careers-filters">
        <div className="container">
          <div className="careers-filters__wrapper">
            <div className="careers-filters__item">
              <RegionSelector
                value={region}
                onChange={handleRegionChange}
              />
            </div>
            <div className="careers-filters__item">
              <CountrySelector
                value={country}
                onChange={handleCountryChange}
              />
            </div>
            {/* <div className="careers-filters__item careers-filters__item--search">
              <SearchBar
                value={keyword}
                onSearch={handleSearch}
                placeholder="Search by job title or location..."
              />
            </div> */}
          </div>
        </div>
      </section>

      <section className="careers-jobs">
        <div className="container">
          {loading && (
            <div className="careers-loading">
              <div className="careers-spinner"></div>
              <p>Loading jobs...</p>
            </div>
          )}

          {error && (
            <div className="careers-error">
              <p>{error}</p>
              <button
                className="careers-btn careers-btn--primary"
                onClick={fetchJobs}
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && jobs.length === 0 && (
            <div className="careers-empty">
              <p>No jobs found. Try adjusting your filters.</p>
            </div>
          )}

          {!loading && !error && jobs.length > 0 && (
            <>
              <JobList jobs={jobs} onJobClick={handleJobClick} />
              {pagination.totalPages > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      </section>

      {showDetail && selectedJob && (
        <JobDetail
          job={selectedJob}
          onClose={() => {
            setShowDetail(false);
            setSelectedJob(null);
          }}
        />
      )}
    </div>
  );
}


